/**
 * pack-builder.js — v2
 *
 * Una decisión a la vez: 1) color, 2) cantidades por talla, 3) agregar al pack.
 * La lista "Tu pack" (chips) acumula colores y vive en la barra inferior
 * pegajosa, junto al progreso y al botón final. El botón nunca se ve muerto:
 * "Elige tus colores" (desactivado, estilo de pista) → "Faltan N" → texto
 * completo (data-texto-completo). Solo envía con el pack exacto.
 *
 * Contratos que no cambian respecto a v1:
 *  - Lee data-stock, data-paleta y data-promos del snippet.
 *  - Line item properties: "Composición" (legible) y "_composicion" (JSON con
 *    variant IDs).
 *  - agregarAlCarrito() es el único punto de reemplazo para la app de bundles.
 *  - Emite 'pack:color' para pack-galeria.js.
 *
 * Promos surtidas (promo.surtido == true): la fábrica elige los colores. No hay
 * paso de color ni botón de agregar; los contadores por talla escriben directo
 * en el pack, que es un único chip "Surtido". El stock de cada talla es la suma
 * de todos los colores. _composicion guarda { surtido: true, tallas: {...} }.
 */

const PB_SURTIDO = 'Surtido';

const PB_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const pbEsc = (valor) => String(valor).replace(/[&<>"']/g, (c) => PB_ESCAPES[c]);

/** Mismo criterio que color_brightness de Liquid (acento-texto.liquid). */
function pbEsClaro(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return true;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 140;
}

const PB_CHECK =
  '<svg class="pack-builder__check" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
  '<path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class PackBuilder extends HTMLElement {
  connectedCallback() {
    if (this.iniciado) return;
    this.iniciado = true;

    this.stock = JSON.parse(this.dataset.stock || '[]');
    this.paleta = JSON.parse(this.dataset.paleta || '[]');
    this.promos = JSON.parse(this.dataset.promos || '[]');

    this.form = this.querySelector('[data-pack-form]');
    this.inputVariante = this.querySelector('[data-pack-variant]');
    this.inputResumen = this.querySelector('[data-pack-resumen]');
    this.inputJson = this.querySelector('[data-pack-json]');
    this.swatches = this.querySelector('[data-pack-swatches]');
    this.pasoColor = this.querySelector('[data-pack-paso-color]');
    this.notaSurtido = this.querySelector('[data-pack-nota-surtido]');
    this.pasoAgregar = this.querySelector('[data-pack-paso-agregar]');
    this.numTallas = this.querySelector('[data-pack-num-tallas]');
    this.detalle = this.querySelector('[data-pack-detalle]');
    this.colorActivoLabel = this.querySelector('[data-pack-color-activo]');
    this.tallas = this.querySelector('[data-pack-tallas]');
    this.botonAgregar = this.querySelector('[data-pack-agregar]');
    this.chips = this.querySelector('[data-pack-chips]');
    this.vacio = this.querySelector('[data-pack-vacio]');
    this.aviso = this.querySelector('[data-pack-aviso]');
    this.progreso = this.querySelector('[data-pack-progreso]');
    this.contador = this.querySelector('[data-pack-contador]');
    this.totalPack = this.querySelector('[data-pack-total]');
    this.submit = this.querySelector('[data-pack-submit]');
    this.error = this.querySelector('[data-pack-error]');
    this.listaTitulo = this.querySelector('.pack-builder__lista-titulo');
    this.uid = `pb-${this.dataset.producto}`;

    // "Negro|XL" -> { cantidad, id }
    this.disponible = new Map();
    this.stock.forEach((s) => this.disponible.set(`${s.c}|${s.t}`, { cantidad: s.q, id: s.id }));
    // "XL" -> unidades sumando todos los colores, para las promos surtidas.
    this.stockTalla = new Map();
    this.stock.forEach((s) => this.stockTalla.set(s.t, (this.stockTalla.get(s.t) || 0) + Math.max(0, s.q)));
    this.hex = new Map(this.paleta.map((c) => [c.nombre, c.hex]));
    this.hex.set(PB_SURTIDO, this.mezclaPaleta());

    this.pack = new Map(); // color -> Map(talla -> unidades), en orden de llegada
    this.borradores = new Map(); // color -> Map(talla -> unidades) del paso 2
    this.colorActivo = null;
    this.recienAgregado = null;

    this.querySelectorAll('[data-pack-promo]').forEach((radio) => {
      radio.addEventListener('change', () => this.cambiarPromo(radio.value, true));
    });

    this.swatches.addEventListener('click', (e) => {
      const boton = e.target.closest('[data-color]');
      if (boton) this.seleccionarColor(boton.dataset.color);
    });
    this.swatches.addEventListener('keydown', (e) => this.teclaSwatch(e));

    this.tallas.addEventListener('click', (e) => {
      const boton = e.target.closest('[data-paso]');
      if (!boton || boton.disabled) return;
      const talla = boton.dataset.talla;
      this.fijarCantidad(talla, (this.borrador.get(talla) || 0) + Number(boton.dataset.paso));
    });
    this.tallas.addEventListener('input', (e) => {
      if (e.target.matches('input[data-talla]')) this.escribirCantidad(e.target);
    });
    this.tallas.addEventListener('change', (e) => {
      if (e.target.matches('input[data-talla]')) this.actualizarTallas();
    });
    this.tallas.addEventListener('keydown', (e) => {
      // Enter en un input numérico enviaría el formulario: lo evitamos.
      if (e.key === 'Enter' && e.target.matches('input[data-talla]')) {
        e.preventDefault();
        this.actualizarTallas();
      }
    });

    this.botonAgregar.addEventListener('click', () => this.agregar());

    this.chips.addEventListener('click', (e) => {
      const boton = e.target.closest('[data-quitar]');
      if (boton) this.quitar(boton.dataset.quitar);
    });

    this.form.addEventListener('submit', (e) => this.enviar(e));

    // En móvil la barra es fija: solo se muestra mientras el constructor está en pantalla.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entrada]) => {
        this.toggleAttribute('data-fuera', !entrada.isIntersecting);
      });
      this.observer.observe(this);
    }

    const inicial = this.promos.find((p) => p.id == this.inputVariante.value);
    this.cambiarPromo(inicial ? inicial.id : this.promos[0]?.id, false);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  // ---------------------------------------------------------------------------
  // Estado
  // ---------------------------------------------------------------------------

  get tallasPromo() {
    return (this.promo && this.promo.tallas) || [];
  }

  get surtido() {
    return Boolean(this.promo && this.promo.surtido);
  }

  get total() {
    let suma = 0;
    this.pack.forEach((porTalla) => porTalla.forEach((n) => (suma += n)));
    return suma;
  }

  get faltan() {
    return this.promo.unidades - this.total;
  }

  get borrador() {
    if (!this.colorActivo) return new Map();
    if (!this.borradores.has(this.colorActivo)) this.borradores.set(this.colorActivo, new Map());
    return this.borradores.get(this.colorActivo);
  }

  get subtotal() {
    let suma = 0;
    this.borrador.forEach((n) => (suma += n));
    return suma;
  }

  stockDe(color, talla) {
    if (color === PB_SURTIDO) return this.stockTalla.get(talla) || 0;
    const item = this.disponible.get(`${color}|${talla}`);
    return item ? Math.max(0, item.cantidad) : 0;
  }

  enPack(color, talla) {
    const porTalla = this.pack.get(color);
    return (porTalla && porTalla.get(talla)) || 0;
  }

  /** min(stock del SKU que no está ya en el pack, unidades que faltan del pack) */
  topeFila(talla) {
    const otros = this.subtotal - (this.borrador.get(talla) || 0);
    // En surtido el borrador ES el pack: no hay nada ya agregado que descontar.
    if (this.surtido) {
      return Math.max(0, Math.min(this.stockDe(PB_SURTIDO, talla), this.promo.unidades - otros));
    }
    const libre = this.stockDe(this.colorActivo, talla) - this.enPack(this.colorActivo, talla);
    return Math.max(0, Math.min(libre, this.faltan - otros));
  }

  /** Swatch multicolor para el chip y la barra de "Surtido". */
  mezclaPaleta() {
    const hexes = this.paleta.map((c) => c.hex).filter(Boolean);
    if (hexes.length < 2) return hexes[0] || '#cccccc';
    const paso = 100 / hexes.length;
    const tramos = hexes.map((h, i) => `${h} ${i * paso}% ${(i + 1) * paso}%`);
    return `linear-gradient(90deg, ${tramos.join(', ')})`;
  }

  /** En surtido el pack refleja el borrador: un solo chip con las tallas en orden. */
  sincronizarSurtido() {
    const borrador = this.borrador;
    const ordenado = new Map(this.tallasPromo.filter((t) => borrador.get(t) > 0).map((t) => [t, borrador.get(t)]));
    this.pack = ordenado.size ? new Map([[PB_SURTIDO, ordenado]]) : new Map();
  }

  /** Deja el estado vacío para el modo de la promo actual. */
  reiniciarModo() {
    this.pack = new Map();
    this.borradores = new Map();
    this.colorActivo = this.surtido ? PB_SURTIDO : null;
    this.recienAgregado = null;
  }

  // ---------------------------------------------------------------------------
  // Promo
  // ---------------------------------------------------------------------------

  cambiarPromo(idVariante, avisar) {
    const anterior = this.promo;
    this.promo = this.promos.find((p) => p.id == idVariante) || this.promos[0];
    if (!this.promo) return;
    this.inputVariante.value = this.promo.id;

    // Entre surtido y por colores el pack no es traducible: se empieza de cero.
    const cambioModo = !anterior || Boolean(anterior.surtido) !== this.surtido;
    let perdio = false;
    let aviso = 'Ajustamos tu pack al nuevo tamaño.';

    if (cambioModo) {
      perdio = this.total > 0 || this.subtotal > 0;
      aviso = this.surtido
        ? 'En esta promo los colores los elige la fábrica: empezamos tu pack de nuevo.'
        : 'En esta promo eliges los colores: empezamos tu pack de nuevo.';
      this.reiniciarModo();
      this.pintarModo();
    } else {
      perdio = this.reescalar();
      if (this.surtido) this.sincronizarSurtido();
    }

    if (!this.surtido && this.colorActivo && !this.tallasPromo.some((t) => this.stockDe(this.colorActivo, t) > 0)) {
      this.colorActivo = null;
    }

    this.pintarSwatches();
    this.pintarTallas();
    this.pintarPack();

    if (avisar && perdio) {
      this.mostrarAviso(aviso);
      this.actualizar(`Cambiaste a la promo de ${this.promo.unidades}. ${aviso}`);
    } else {
      this.ocultarAviso();
      this.actualizar(avisar ? `Cambiaste a la promo de ${this.promo.unidades}.` : '');
    }
  }

  /**
   * Conserva lo elegido si la talla sigue existiendo y recorta lo que sobra,
   * respetando el orden en que se agregó. Devuelve true si se perdió algo.
   */
  reescalar() {
    const tallas = this.tallasPromo;
    const conservado = new Map();
    let acumulado = 0;
    let perdio = false;

    this.pack.forEach((porTalla, color) => {
      const nuevo = new Map();
      porTalla.forEach((n, talla) => {
        if (!tallas.includes(talla)) {
          perdio = true;
          return;
        }
        const cabe = Math.min(n, this.promo.unidades - acumulado);
        if (cabe < n) perdio = true;
        if (cabe > 0) {
          nuevo.set(talla, cabe);
          acumulado += cabe;
        }
      });
      if (nuevo.size) conservado.set(color, nuevo);
    });
    this.pack = conservado;

    this.borradores.forEach((borrador, color) => {
      borrador.forEach((n, talla) => {
        if (tallas.includes(talla)) return;
        borrador.delete(talla);
        if (color === this.colorActivo && n > 0) perdio = true;
      });
    });

    if (this.normalizarBorrador()) perdio = true;
    return perdio;
  }

  /** Recorta el borrador activo a los topes vigentes. Devuelve true si recortó. */
  normalizarBorrador() {
    if (!this.colorActivo) return false;
    let recorto = false;
    this.tallasPromo.forEach((talla) => {
      const n = this.borrador.get(talla) || 0;
      if (!n) return;
      const tope = this.topeFila(talla);
      if (n > tope) {
        recorto = true;
        if (tope > 0) this.borrador.set(talla, tope);
        else this.borrador.delete(talla);
      }
    });
    return recorto;
  }

  /** Muestra u oculta los pasos que dependen del modo (surtido o por colores). */
  pintarModo() {
    const surtido = this.surtido;
    this.pasoColor.hidden = surtido;
    if (this.notaSurtido) this.notaSurtido.hidden = !surtido;
    this.pasoAgregar.hidden = surtido;
    this.numTallas.textContent = surtido ? '1' : '2';
    this.vacio.textContent = surtido ? 'Aún no has elegido tallas' : 'Aún no has agregado colores';
    this.ocultarAviso();
    this.error.hidden = true;

    // La galería vuelve a la foto principal: en surtido no hay color que mostrar.
    document.dispatchEvent(
      new CustomEvent('pack:color', { detail: { producto: this.dataset.producto, color: null } })
    );
  }

  // ---------------------------------------------------------------------------
  // Paso 1 · Color
  // ---------------------------------------------------------------------------

  pintarSwatches() {
    if (this.surtido) {
      this.swatches.innerHTML = '';
      return;
    }

    const tallas = this.tallasPromo;
    const colores = this.paleta.filter((c) => tallas.some((t) => this.stockDe(c.nombre, t) > 0));

    if (!colores.length) {
      this.swatches.innerHTML =
        '<p class="pack-builder__sin-stock">No hay stock disponible para esta promo.</p>';
      return;
    }

    const conFoco = colores.some((c) => c.nombre === this.colorActivo) ? this.colorActivo : colores[0].nombre;

    this.swatches.innerHTML = colores
      .map((c) => {
        const activo = c.nombre === this.colorActivo;
        return `<button
            type="button"
            role="radio"
            class="pack-builder__swatch${pbEsClaro(c.hex) ? ' is-claro' : ''}"
            aria-checked="${activo}"
            tabindex="${c.nombre === conFoco ? 0 : -1}"
            data-color="${pbEsc(c.nombre)}"
          ><span class="pack-builder__swatch-circulo" style="--swatch: ${pbEsc(c.hex)}">${PB_CHECK}</span
          ><span class="pack-builder__swatch-nombre">${pbEsc(c.nombre)}</span></button>`;
      })
      .join('');
  }

  botonesSwatch() {
    return Array.from(this.swatches.querySelectorAll('[data-color]'));
  }

  teclaSwatch(e) {
    const botones = this.botonesSwatch();
    const actual = botones.indexOf(e.target.closest('[data-color]'));
    if (actual === -1) return;

    // Columnas reales de la grilla fluida: cuántos comparten la fila del primero.
    const top = botones[0].offsetTop;
    const columnas = Math.max(1, botones.filter((b) => b.offsetTop === top).length);

    let destino = null;
    switch (e.key) {
      case 'ArrowRight':
        destino = (actual + 1) % botones.length;
        break;
      case 'ArrowLeft':
        destino = (actual - 1 + botones.length) % botones.length;
        break;
      case 'ArrowDown':
        destino = Math.min(botones.length - 1, actual + columnas);
        break;
      case 'ArrowUp':
        destino = Math.max(0, actual - columnas);
        break;
      case 'Home':
        destino = 0;
        break;
      case 'End':
        destino = botones.length - 1;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.seleccionarColor(botones[actual].dataset.color);
        return;
      default:
        return;
    }

    e.preventDefault();
    botones.forEach((b, i) => (b.tabIndex = i === destino ? 0 : -1));
    botones[destino].focus();
  }

  seleccionarColor(color) {
    this.colorActivo = color;
    this.normalizarBorrador();

    this.botonesSwatch().forEach((b) => {
      const activo = b.dataset.color === color;
      b.setAttribute('aria-checked', String(activo));
      b.tabIndex = activo ? 0 : -1;
    });

    this.pintarTallas();

    document.dispatchEvent(
      new CustomEvent('pack:color', { detail: { producto: this.dataset.producto, color } })
    );
  }

  // ---------------------------------------------------------------------------
  // Paso 2 · Cantidades por talla
  // ---------------------------------------------------------------------------

  pintarTallas() {
    if (!this.colorActivo) {
      this.detalle.hidden = true;
      this.tallas.innerHTML = '';
      return;
    }

    const color = this.colorActivo;
    this.detalle.hidden = false;
    this.colorActivoLabel.textContent = this.surtido ? '' : ` · ${color}`;

    const filas = this.tallasPromo
      .map((talla) => {
        const stock = this.stockDe(color, talla);
        if (stock === 0) {
          return `<div class="pack-builder__talla is-agotada">
              <span class="pack-builder__talla-nombre">${pbEsc(talla)}</span>
              <span class="pack-builder__talla-estado">agotado</span>
            </div>`;
        }

        const inputId = `${this.uid}-${talla.replace(/[^a-z0-9]/gi, '')}`;
        const escasez =
          stock < 10 ? `<span class="pack-builder__escasez">quedan ${stock}</span>` : '';

        return `<div class="pack-builder__talla${escasez ? ' has-escasez' : ''}" data-fila="${pbEsc(talla)}">
            <label class="pack-builder__talla-nombre" for="${inputId}">${pbEsc(talla)}</label>
            ${escasez}
            <div class="pack-builder__stepper">
              <button type="button" class="pack-builder__paso-btn" data-paso="-1" data-talla="${pbEsc(talla)}"
                aria-label="Quitar una ${pbEsc(color)} talla ${pbEsc(talla)}">−</button>
              <input type="number" inputmode="numeric" min="0" step="1" value="0" id="${inputId}"
                data-talla="${pbEsc(talla)}" aria-label="${pbEsc(color)} talla ${pbEsc(talla)}, unidades">
              <button type="button" class="pack-builder__paso-btn" data-paso="1" data-talla="${pbEsc(talla)}"
                aria-label="Agregar una ${pbEsc(color)} talla ${pbEsc(talla)}">+</button>
            </div>
          </div>`;
      })
      .join('');

    this.tallas.innerHTML =
      '<p class="pack-builder__nota" data-pack-lleno hidden>Tu pack ya está completo. Quita un color de la lista para cambiarlo.</p>' +
      filas;

    this.actualizarTallas();
  }

  /** Refresca valores, topes y botones sin reconstruir el DOM (conserva el foco). */
  actualizarTallas() {
    if (!this.colorActivo) return;

    this.tallas.querySelectorAll('[data-fila]').forEach((fila) => {
      const talla = fila.dataset.fila;
      const valor = this.borrador.get(talla) || 0;
      const tope = this.topeFila(talla);
      const input = fila.querySelector('input');

      input.max = String(tope);
      // No pisar lo que el cliente está escribiendo (p. ej. el campo vacío a medio borrar).
      const escribiendo = document.activeElement === input;
      if (!escribiendo || (input.value !== '' && Number(input.value) !== valor)) {
        input.value = String(valor);
      }
      fila.querySelector('[data-paso="-1"]').disabled = valor <= 0;
      fila.querySelector('[data-paso="1"]').disabled = valor >= tope;
    });

    const lleno = this.tallas.querySelector('[data-pack-lleno]');
    if (lleno) lleno.hidden = !(this.faltan === 0 && this.subtotal === 0);

    const subtotal = this.subtotal;
    this.botonAgregar.disabled = subtotal === 0;
    this.botonAgregar.textContent = subtotal > 0 ? `+ Sumar al pack (${subtotal})` : '+ Sumar al pack';
  }

  fijarCantidad(talla, valor) {
    const tope = this.topeFila(talla);
    const n = Math.max(0, Math.min(Number.isFinite(valor) ? valor : 0, tope));
    if (n > 0) this.borrador.set(talla, n);
    else this.borrador.delete(talla);
    this.cambiaronCantidades();
  }

  /** En surtido cada cambio va directo al pack; por colores espera al paso 3. */
  cambiaronCantidades() {
    if (!this.surtido) {
      this.actualizarTallas();
      return;
    }
    this.sincronizarSurtido();
    this.ocultarAviso();
    this.pintarPack();
    this.actualizar();
  }

  escribirCantidad(input) {
    const talla = input.dataset.talla;
    const escrito = parseInt(input.value, 10);
    const tope = this.topeFila(talla);
    const n = Math.max(0, Math.min(Number.isNaN(escrito) ? 0 : escrito, tope));

    if (n > 0) this.borrador.set(talla, n);
    else this.borrador.delete(talla);

    // Corrige lo escrito solo si se pasó del tope o no es un número válido.
    if (input.value !== '' && String(n) !== input.value) input.value = String(n);
    this.cambiaronCantidades();
  }

  // ---------------------------------------------------------------------------
  // Paso 3 · Agregar al pack
  // ---------------------------------------------------------------------------

  agregar() {
    const color = this.colorActivo;
    if (this.surtido || !color || this.subtotal === 0) return;

    const destino = this.pack.get(color) || new Map();
    this.borrador.forEach((n, talla) => destino.set(talla, (destino.get(talla) || 0) + n));
    // Orden de tallas según la promo, para chips y resumen.
    const ordenado = new Map(
      this.tallasPromo.filter((t) => destino.has(t)).map((t) => [t, destino.get(t)])
    );
    this.pack.set(color, ordenado);

    const detalle = this.textoTallas(ordenado);
    this.borradores.delete(color);
    this.colorActivo = null;
    this.recienAgregado = color;

    this.ocultarAviso();
    this.pintarSwatches();
    this.pintarTallas();
    this.pintarPack();
    this.actualizar(`Agregaste ${color}, ${detalle}.`);

    // El foco vuelve a los swatches, sobre el color recién agregado.
    const swatch = this.botonesSwatch().find((b) => b.dataset.color === color) || this.botonesSwatch()[0];
    if (swatch) {
      this.botonesSwatch().forEach((b) => (b.tabIndex = b === swatch ? 0 : -1));
      swatch.focus();
    }
  }

  quitar(color) {
    const orden = Array.from(this.pack.keys());
    const i = orden.indexOf(color);
    if (i === -1) return;

    this.pack.delete(color);
    if (this.surtido) {
      // El chip "Surtido" es el borrador: quitarlo vacía los contadores.
      this.borradores.delete(PB_SURTIDO);
      this.pintarTallas();
    }
    this.ocultarAviso();
    this.pintarPack();
    this.actualizarTallas();
    this.actualizar(this.surtido ? 'Vaciaste tu pack.' : `Quitaste ${color}.`);

    // Foco al chip siguiente, al anterior o al título de la lista: nunca al vacío.
    const restantes = Array.from(this.chips.querySelectorAll('[data-quitar]'));
    const siguiente = restantes[i] || restantes[i - 1];
    if (siguiente) siguiente.focus();
    else this.listaTitulo.focus();
  }

  textoTallas(porTalla) {
    return Array.from(porTalla.entries())
      .map(([talla, n]) => `${talla}×${n}`)
      .join(' ');
  }

  // ---------------------------------------------------------------------------
  // Lista "Tu pack" y barra
  // ---------------------------------------------------------------------------

  pintarPack() {
    this.chips.innerHTML = Array.from(this.pack.entries())
      .map(([color, porTalla]) => {
        const hex = this.hex.get(color) || '#cccccc';
        const nuevo = color === this.recienAgregado ? ' is-nuevo' : '';
        return `<li class="pack-builder__chip${nuevo}">
            <span class="pack-builder__chip-swatch" style="--swatch: ${pbEsc(hex)}"></span>
            <span class="pack-builder__chip-texto">${pbEsc(color)} · ${pbEsc(this.textoTallas(porTalla))}</span>
            <button type="button" class="pack-builder__chip-quitar" data-quitar="${pbEsc(color)}"
              aria-label="Quitar ${pbEsc(color)} del pack">✕</button>
          </li>`;
      })
      .join('');

    this.recienAgregado = null;
    this.vacio.hidden = this.pack.size > 0;
  }

  actualizar(anuncio = '') {
    const unidades = this.promo.unidades;
    const total = this.total;
    const completo = total === unidades;
    const texto = completo ? `${unidades} de ${unidades} · listo` : `${total} de ${unidades} · faltan ${unidades - total}`;

    this.contador.innerHTML =
      (anuncio ? `<span class="visually-hidden">${pbEsc(anuncio)} </span>` : '') +
      pbEsc(texto);
    if (this.totalPack) {
      const precio = this.promo.precio_corto || this.promo.precio;
      this.totalPack.textContent = precio ? `Pack ${precio}` : '';
    }
    this.contador.classList.toggle('is-completo', completo);

    this.progreso.innerHTML = Array.from(this.pack.entries())
      .map(([color, porTalla]) => {
        let n = 0;
        porTalla.forEach((v) => (n += v));
        const ancho = (n / unidades) * 100;
        return `<span class="pack-builder__segmento" style="--segmento: ${pbEsc(this.hex.get(color) || '#cccccc')}; width: ${ancho}%"></span>`;
      })
      .join('');

    this.pintarBoton();
    this.error.hidden = true;
    this.actualizarTallas();
  }

  /** Texto y estado del botón final según cuánto falta. */
  pintarBoton() {
    const total = this.total;
    const faltan = this.promo.unidades - total;
    let texto;
    if (total === 0) texto = this.surtido ? 'Elige tus tallas' : 'Elige tus colores';
    else if (faltan > 0) texto = `Faltan ${faltan}`;
    else texto = this.submit.dataset.textoCompleto || 'Agregar al pack';

    this.submit.textContent = texto;
    this.submit.disabled = total === 0;
    this.submit.classList.toggle('is-pista', faltan > 0);
  }

  /** Con el pack a medias el botón no da error: lleva al paso que sigue. */
  guiar() {
    const faltan = this.promo.unidades - this.total;
    const destino = this.surtido
      ? this.tallas.querySelector('[data-fila] input')
      : this.botonesSwatch().find((b) => b.tabIndex === 0) || this.botonesSwatch()[0];
    if (destino) {
      destino.scrollIntoView({ behavior: 'smooth', block: 'center' });
      destino.focus({ preventScroll: true });
    }
    this.actualizar(this.surtido ? `Te faltan ${faltan}: súmalos en las tallas.` : `Te faltan ${faltan}: elige otro color.`);
  }

  mostrarAviso(mensaje) {
    this.aviso.textContent = mensaje;
    this.aviso.hidden = false;
  }

  ocultarAviso() {
    this.aviso.hidden = true;
    this.aviso.textContent = '';
  }

  // ---------------------------------------------------------------------------
  // Envío (contrato de v1)
  // ---------------------------------------------------------------------------

  /** Pares [color, talla, unidades] en el orden del pack y de las tallas. */
  lineas() {
    const salida = [];
    this.pack.forEach((porTalla, color) => {
      porTalla.forEach((n, talla) => salida.push([color, talla, n]));
    });
    return salida;
  }

  /** Texto legible para el picking: "Negro XL ×4, Blanco 2XL ×3" */
  resumen() {
    // Surtido: "Surtido · XS×2 S×4 M×5 L×4"
    if (this.surtido) return `${PB_SURTIDO} · ${this.textoTallas(this.pack.get(PB_SURTIDO) || new Map())}`;
    return this.lineas()
      .map(([color, talla, n]) => `${color} ${talla} ×${n}`)
      .join(', ');
  }

  /** JSON con variant IDs reales, para automatizaciones y la migración a bundles */
  composicion() {
    // Surtido: no hay variantes de color que referenciar, solo tallas.
    if (this.surtido) {
      return { surtido: true, tallas: Object.fromEntries(this.pack.get(PB_SURTIDO) || []) };
    }
    return this.lineas().map(([color, talla, n]) => ({
      color,
      talla,
      cantidad: n,
      variante: this.disponible.get(`${color}|${talla}`).id,
    }));
  }

  async enviar(e) {
    e.preventDefault();

    if (this.total !== this.promo.unidades) {
      this.guiar();
      return;
    }

    this.inputResumen.value = this.resumen();
    this.inputJson.value = JSON.stringify(this.composicion());

    this.submit.disabled = true;
    this.submit.classList.add('is-cargando');

    try {
      await this.agregarAlCarrito();
    } catch (err) {
      this.mostrarError('No pudimos agregar el pack. Intenta de nuevo.');
      console.error(err);
    } finally {
      this.submit.classList.remove('is-cargando');
      this.pintarBoton();
    }
  }

  limpiar() {
    this.reiniciarModo();
    this.pintarSwatches();
    this.pintarTallas();
    this.pintarPack();
    this.actualizar('');
  }

  /**
   * ---------------------------------------------------------------------------
   * PUNTO DE REEMPLAZO
   * Cuando migremos a app de bundles, solo cambia este método.
   * ---------------------------------------------------------------------------
   */
  async agregarAlCarrito() {
    const cuerpo = new FormData(this.form);
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      cuerpo.append('sections', 'cart-drawer,cart-icon-bubble');
      cuerpo.append('sections_url', window.location.pathname);
    }

    const respuesta = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: cuerpo,
    });

    if (!respuesta.ok) throw new Error(`cart/add devolvió ${respuesta.status}`);
    const datos = await respuesta.json();

    this.limpiar();
    document.dispatchEvent(new CustomEvent('pack:agregado', { detail: datos }));

    // Abre el cart drawer de Dawn si está presente; si no, va al carrito.
    if (drawer && typeof drawer.renderContents === 'function' && datos.sections) {
      drawer.renderContents(datos);
    } else {
      window.location.href = window.Shopify.routes.root + 'cart';
    }
  }

  mostrarError(mensaje) {
    this.error.textContent = mensaje;
    this.error.hidden = false;
  }
}

if (!customElements.get('pack-builder')) {
  customElements.define('pack-builder', PackBuilder);
}
